import { useMemo } from "react";
import { geoOrthographic, geoPath, type GeoPermissibleObjects } from "d3-geo";
import { SAMPLE_TRIP } from "./fixtures";
import landGeo from "@/assets/geo/land-110m.json";

/**
 * Stylized SVG globe approximating the real Mapbox `globe`-projection map
 * used in the Route tab. Not a live map (too heavy for the onboarding
 * sheet), but geographically real: Natural Earth 110m land outlines
 * projected with d3-geo's orthographic projection, centered on Mongolia
 * (~103°E, 47°N). Sample Mongolia route is drawn as a glowing arc with
 * yellow pod pins at Ulaanbaatar and Kharkhorin.
 */
export function RouteGlobePreview() {
  const w = 420;
  const h = 260;
  const cx = w / 2;
  const cy = h / 2 + 4;
  const r = 110; // sphere radius

  // Orthographic projection centered on Mongolia. Matches the previous
  // hand-rolled projection so existing pin/arc positions look consistent.
  const projection = useMemo(
    () =>
      geoOrthographic()
        .scale(r)
        .translate([cx, cy])
        .rotate([-103, -47, 0])
        .clipAngle(90),
    [cx, cy, r],
  );

  const pathGen = useMemo(() => geoPath(projection), [projection]);
  const landPath = useMemo(
    () => pathGen(landGeo as unknown as GeoPermissibleObjects) ?? "",
    [pathGen],
  );

  // Project a lng/lat point; null when on the far hemisphere.
  const project = (lng: number, lat: number) => {
    const p = projection([lng, lat]);
    return p ? { x: p[0], y: p[1] } : null;
  };

  // Build a smooth arc through the route's first/last points, lifted toward
  // the viewer via a quadratic control on the projected midpoint.
  const projected = SAMPLE_TRIP.route
    .map((p) => project(p.lng, p.lat))
    .filter((p): p is { x: number; y: number } => !!p);
  const first = projected[0];
  const last = projected[projected.length - 1];
  const midRaw = projected[Math.floor(projected.length / 2)];
  const ctrl = { x: midRaw.x, y: midRaw.y - 22 };
  const arcPath = `M ${first.x} ${first.y} Q ${ctrl.x} ${ctrl.y} ${last.x} ${last.y}`;

  // Pod pin positions sampled along the same quadratic.
  const pinT = [0.05, 0.5, 0.95];
  const samplePoint = (t: number) => ({
    x:
      (1 - t) * (1 - t) * first.x + 2 * (1 - t) * t * ctrl.x + t * t * last.x,
    y:
      (1 - t) * (1 - t) * first.y + 2 * (1 - t) * t * ctrl.y + t * t * last.y,
  });
  const pins = pinT.map((t) => samplePoint(t));

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Deep-space backdrop */}
        <radialGradient id="space-bg" cx="50%" cy="50%" r="75%">
          <stop offset="0%" stopColor="#0b1726" />
          <stop offset="100%" stopColor="#05080f" />
        </radialGradient>
        {/* Ocean sphere with subtle terminator shading */}
        <radialGradient id="ocean" cx="42%" cy="38%" r="65%">
          <stop offset="0%" stopColor="#2b6fb0" />
          <stop offset="55%" stopColor="#1a3f72" />
          <stop offset="100%" stopColor="#0a1d3a" />
        </radialGradient>
        {/* Atmosphere halo */}
        <radialGradient id="atmo" cx="50%" cy="50%" r="50%">
          <stop offset="78%" stopColor="#60a5fa" stopOpacity="0" />
          <stop offset="92%" stopColor="#7dd3fc" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#7dd3fc" stopOpacity="0" />
        </radialGradient>
        {/* Route gradient */}
        <linearGradient id="route-stroke" x1="0" x2="1">
          <stop offset="0%" stopColor="#60a5fa" />
          <stop offset="100%" stopColor="#34d399" />
        </linearGradient>
        <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="3.5" />
        </filter>
        {/* Specular highlight on the sphere */}
        <radialGradient id="specular" cx="35%" cy="28%" r="35%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
        {/* Clip everything geographic to the sphere */}
        <clipPath id="sphere-clip">
          <circle cx={cx} cy={cy} r={r} />
        </clipPath>
      </defs>

      {/* Space */}
      <rect width={w} height={h} fill="url(#space-bg)" />

      {/* Atmosphere glow (drawn larger than the sphere) */}
      <circle cx={cx} cy={cy} r={r + 18} fill="url(#atmo)" />

      {/* Ocean sphere */}
      <circle cx={cx} cy={cy} r={r} fill="url(#ocean)" />

      {/* Real land outlines from Natural Earth 110m, clipped to sphere. */}
      <g clipPath="url(#sphere-clip)">
        <path
          d={landPath}
          fill="#3f5a3a"
          fillOpacity="0.82"
          stroke="#2a3d28"
          strokeOpacity="0.5"
          strokeWidth="0.4"
        />
      </g>

      {/* Latitude / meridian rings clipped to sphere */}
      <g
        clipPath="url(#sphere-clip)"
        stroke="#ffffff"
        strokeOpacity="0.12"
        fill="none"
        strokeWidth="0.8"
      >
        <ellipse cx={cx} cy={cy - 60} rx={r * 0.85} ry={r * 0.18} />
        <ellipse cx={cx} cy={cy - 30} rx={r * 0.96} ry={r * 0.22} />
        <ellipse cx={cx} cy={cy} rx={r} ry={r * 0.24} />
        <ellipse cx={cx} cy={cy + 30} rx={r * 0.96} ry={r * 0.22} />
        <ellipse cx={cx} cy={cy + 60} rx={r * 0.85} ry={r * 0.18} />
        <ellipse cx={cx} cy={cy} rx={r * 0.22} ry={r} />
        <ellipse cx={cx} cy={cy} rx={r * 0.5} ry={r} />
        <ellipse cx={cx} cy={cy} rx={r * 0.78} ry={r} />
      </g>

      {/* Sphere edge ring */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#7dd3fc"
        strokeOpacity="0.35"
        strokeWidth="1"
      />

      {/* Specular highlight */}
      <circle cx={cx} cy={cy} r={r} fill="url(#specular)" />

      {/* Route — glow + crisp line */}
      <g clipPath="url(#sphere-clip)">
        <path
          d={arcPath}
          fill="none"
          stroke="url(#route-stroke)"
          strokeWidth="6"
          strokeLinecap="round"
          opacity="0.55"
          filter="url(#glow)"
        />
        <path
          d={arcPath}
          fill="none"
          stroke="url(#route-stroke)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />

        {/* Pod pins */}
        {pins.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="9" fill="#ffffff" opacity="0.18" />
            <circle
              cx={p.x}
              cy={p.y}
              r="4.5"
              fill="#fbbf24"
              stroke="#ffffff"
              strokeWidth="1.5"
            />
          </g>
        ))}
      </g>

      {/* End-cap labels (outside clip so they can sit just off the limb) */}
      <text
        x={first.x + 10}
        y={first.y - 8}
        fill="#e2e8f0"
        fontSize="11"
        fontWeight="600"
      >
        Ulaanbaatar
      </text>
      <text
        x={last.x - 70}
        y={last.y + 16}
        fill="#e2e8f0"
        fontSize="11"
        fontWeight="600"
      >
        Kharkhorin
      </text>
    </svg>
  );
}
